// ─────────────────────────────────────────────────────────────────────────────
// Editor de texto com imagem embutida.
//
// Substitui o <textarea> das anotações. O motivo é único: textarea só renderiza
// caracteres, então o print colado no meio da frase apareceria como
// `[[anexo:0f3c…]]` — o marcador, não a imagem.
//
// O QUE ISTO **NÃO** MUDA
// O valor continua sendo uma STRING com marcadores, igual ao que sempre foi
// enviado ao n8n e gravado no histórico. A API é a do textarea (`value` /
// `onChange` / `onBlur`), então quem usa não precisa saber que mudou por dentro.
//
// A REGRA QUE SUSTENTA TUDO: O DOM MANDA ENQUANTO SE DIGITA.
// Um contenteditable "controlado" pelo React — em que cada tecla reescreve o
// innerHTML — perde o cursor a cada letra e o texto sai ao contrário. Aqui o
// DOM só é reconstruído quando o `value` chega DE FORA diferente do que está na
// tela (formalizar com a IA, reutilizar do histórico, descartar).
//
// Portado do EditorTexto.js do Nexus.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { partesDoTexto } from '../services/textoComAnexos';

// ── DOM → texto ──────────────────────────────────────────────────────────────
//
// O navegador tem liberdade para representar a mesma coisa de várias formas
// (Enter vira <div>, <br> ou <p> conforme o browser e o histórico da edição).
// Por isso a leitura é por SEMÂNTICA, não por estrutura: o que interessa é onde
// há quebra de linha e onde há imagem.

const ehBloco = (tag) => tag === 'DIV' || tag === 'P' || tag === 'LI';

function lerNo(no, saida) {
  if (no.nodeType === 3) {                 // texto
    // O navegador usa &nbsp; ao digitar espaços seguidos. Gravado, vira um
    // caractere invisível que quebra busca e comparação.
    saida.push(String(no.nodeValue || '').replace(/ /g, ' '));
    return;
  }
  if (no.nodeType !== 1) return;           // comentário e afins

  const tag = no.tagName;

  // Anexo: o elemento inteiro vale por um marcador, e não se desce nos filhos.
  const idAnexo = no.getAttribute && no.getAttribute('data-anexo');
  if (idAnexo) { saida.push(`[[anexo:${idAnexo}]]`); return; }

  if (tag === 'BR') { saida.push('\n'); return; }

  const bloco = ehBloco(tag);
  const anterior = saida[saida.length - 1];
  // Bloco começa em linha nova — a menos que já estejamos numa.
  if (bloco && saida.length && anterior !== undefined && !anterior.endsWith('\n')) {
    saida.push('\n');
  }

  no.childNodes.forEach(filho => lerNo(filho, saida));
}

export function serializar(el) {
  if (!el) return '';
  const saida = [];
  el.childNodes.forEach(no => lerNo(no, saida));
  return saida.join('')
    // O navegador adiciona um <br> "fantasma" no fim de bloco vazio; ele não é
    // conteúdo e não pode virar uma linha em branco a cada gravação.
    .replace(/\n+$/, '');
}

/** Quantos caracteres do texto serializado existem até um ponto do DOM. */
function offsetAte(el, container, offset) {
  const ate = document.createRange();
  ate.selectNodeContents(el);
  ate.setEnd(container, offset);
  const molde = document.createElement('div');
  molde.appendChild(ate.cloneContents());
  return serializar(molde).length;
}

/**
 * Posição do cursor em CARACTERES do texto serializado — é assim que o resto do
 * sistema fala de posição (`inserirMarcador` recebe um índice de string).
 */
export function offsetDoCursor(el) {
  const sel = typeof window !== 'undefined' && window.getSelection && window.getSelection();
  if (!el || !sel || !sel.rangeCount) return null;
  const range = sel.getRangeAt(0);
  if (!el.contains(range.startContainer)) return null;
  return offsetAte(el, range.startContainer, range.startOffset);
}

/**
 * Começo e fim da seleção, em caracteres do texto serializado.
 * É o que permite aplicar negrito/itálico sobre o que está marcado — o
 * contenteditable não tem selectionStart/selectionEnd como o textarea.
 */
export function selecaoDoTexto(el) {
  const sel = typeof window !== 'undefined' && window.getSelection && window.getSelection();
  if (!el || !sel || !sel.rangeCount) return null;
  const range = sel.getRangeAt(0);
  if (!el.contains(range.startContainer) || !el.contains(range.endContainer)) return null;
  return {
    inicio: offsetAte(el, range.startContainer, range.startOffset),
    fim: offsetAte(el, range.endContainer, range.endOffset),
  };
}

// ── texto → DOM ──────────────────────────────────────────────────────────────

function noDeAnexo(id, imagem) {
  if (imagem && imagem.dataUrl) {
    const img = document.createElement('img');
    img.src = imagem.dataUrl;
    img.alt = imagem.legenda || imagem.nome || 'imagem';
    img.title = imagem.legenda || imagem.nome || '';
    img.className = 'anx-inline';
    img.setAttribute('data-anexo', id);
    // `contenteditable=false` é o que impede o cursor de entrar "dentro" da
    // imagem e o Backspace de apagá-la pela metade.
    img.setAttribute('contenteditable', 'false');
    img.draggable = false;
    return img;
  }

  // Imagem que já não existe (removida da lista): uma etiqueta, não um
  // retângulo quebrado. Some do texto o id, que não diz nada a quem escreve.
  const chip = document.createElement('span');
  chip.className = 'anx-chip';
  chip.setAttribute('data-anexo', id);
  chip.setAttribute('contenteditable', 'false');
  chip.textContent = 'imagem removida';
  return chip;
}

export function montarDom(el, texto, imagens = {}) {
  if (!el) return;
  el.innerHTML = '';
  const partes = partesDoTexto(texto);

  partes.forEach((parte) => {
    if (parte.tipo === 'anexo') {
      el.appendChild(noDeAnexo(parte.id, imagens[parte.id]));
      return;
    }
    // Cada \n vira um <br>: é a representação que sobrevive à edição em todos
    // os navegadores sem virar um aninhamento de <div>.
    const linhas = String(parte.valor).split('\n');
    linhas.forEach((linha, i) => {
      if (i > 0) el.appendChild(document.createElement('br'));
      if (linha) el.appendChild(document.createTextNode(linha));
    });
  });

  // ÂNCORA DEPOIS DA ÚLTIMA IMAGEM.
  //
  // Um elemento `contenteditable=false` no fim do campo não deixa o cursor
  // pousar depois dele — não há nó nenhum ali. O efeito prático era: colar uma
  // imagem e não conseguir escrever embaixo dela.
  //
  // O <br> resolve dando esse lugar, e não suja o texto: `serializar` já corta
  // as quebras do fim.
  const ultimo = el.lastChild;
  if (ultimo && ultimo.nodeType === 1 && ultimo.getAttribute
      && ultimo.getAttribute('data-anexo')) {
    el.appendChild(document.createElement('br'));
  }
}

/**
 * Põe o cursor num offset de caractere. Usado depois de inserir uma imagem,
 * para a pessoa continuar escrevendo de onde parou.
 */
export function posicionarCursor(el, offset) {
  if (!el || typeof window === 'undefined' || !window.getSelection) return;
  const sel = window.getSelection();
  const range = document.createRange();

  // Caminha pelos nós somando o que cada um vale em caracteres, até chegar ao
  // ponto pedido.
  let restante = Math.max(0, offset);
  let alvo = null;

  const visitar = (no) => {
    if (alvo) return;
    if (no.nodeType === 3) {
      const tam = String(no.nodeValue || '').length;
      if (restante <= tam) { alvo = { no, pos: restante }; return; }
      restante -= tam;
      return;
    }
    if (no.nodeType !== 1) return;
    const idAnexo = no.getAttribute && no.getAttribute('data-anexo');
    if (idAnexo) { restante -= `[[anexo:${idAnexo}]]`.length; return; }
    if (no.tagName === 'BR') { restante -= 1; return; }
    no.childNodes.forEach(visitar);
  };
  el.childNodes.forEach(visitar);

  try {
    if (alvo) range.setStart(alvo.no, alvo.pos);
    else { range.selectNodeContents(el); range.collapse(false); }
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  } catch (e) {
    // Offset fora do que existe no DOM (o texto mudou no meio do caminho):
    // deixar o cursor onde está é melhor que estourar na cara de quem digita.
  }
}

// ── componente ───────────────────────────────────────────────────────────────

const EditorTexto = React.forwardRef(function EditorTexto({
  value = '',
  imagens = {},       // { id: { dataUrl, legenda, nome } }
  onChange,
  onBlur,
  onArquivo,          // (File) => void — colar ou arrastar
  onKeyDown,
  placeholder = '',
  disabled = false,
  className = '',
  style,
  ...resto
}, refExterna) {
  const interno = useRef(null);
  const el = () => interno.current;

  // Último `value` que ESTE editor emitiu. É a memória que distingue "o texto
  // mudou porque digitaram aqui" de "o texto mudou lá fora" — e é o que impede
  // o DOM de ser reconstruído a cada tecla.
  const emitido = useRef(null);
  // Onde o cursor (e a seleção) estavam quando o foco saiu — clicar num botão
  // da barra tira o foco do campo, e sem isso a formatação cairia no fim.
  const cursorGuardado = useRef(null);
  const selecaoGuardada = useRef(null);

  // Chave que resume as imagens exibíveis: quando uma imagem é anexada ou
  // removida, o DOM precisa ser refeito para a figura aparecer ou sair.
  const chaveImagens = Object.keys(imagens)
    .map(id => `${id}:${imagens[id]?.dataUrl ? 1 : 0}`)
    .sort()
    .join('|');

  // O mapa completo entra por ref, e não como dependência: ele é um objeto novo
  // a cada render do pai, e reconstruir o DOM a cada render mataria o cursor.
  const imagensRef = useRef(imagens);
  imagensRef.current = imagens;

  // Qual conjunto de imagens já está desenhado. Sem esta memória, a guarda
  // abaixo (que existe para não destruir o cursor a cada tecla) também barraria
  // a remontagem quando SÓ as imagens mudassem.
  const chaveDesenhada = useRef(null);

  useEffect(() => {
    const alvo = el();
    if (!alvo) return;
    const atual = serializar(alvo);
    // Veio de dentro, e as imagens são as mesmas: não encostar no DOM.
    const textoIgual = atual === value && emitido.current === value;
    if (textoIgual && chaveDesenhada.current === chaveImagens) return;

    // Remontar destrói o cursor. Guardar a posição aqui é o que permite anexar
    // uma imagem e continuar escrevendo logo depois dela.
    const tinhaFoco = typeof document !== 'undefined' && document.activeElement === alvo;
    const cursor = tinhaFoco ? offsetDoCursor(alvo) : null;

    montarDom(alvo, value, imagensRef.current);
    emitido.current = value;
    chaveDesenhada.current = chaveImagens;

    if (tinhaFoco) {
      alvo.focus();
      if (cursor !== null) posicionarCursor(alvo, cursor);
    }
  }, [value, chaveImagens]);

  const emitir = useCallback((gravar) => {
    const alvo = el();
    if (!alvo) return;
    const texto = serializar(alvo);
    emitido.current = texto;
    onChange?.({ target: { value: texto } });
    if (gravar) onBlur?.({ target: { value: texto } });
  }, [onChange, onBlur]);

  // Colar: imagem vira anexo; o resto entra como TEXTO PURO. Colar de um site
  // ou do Word traria tabelas, fontes e cores para dentro de um campo que vira
  // string — e tudo isso viraria lixo na serialização.
  const aoColar = (e) => {
    if (disabled) return;
    const dt = e.clipboardData;
    const arquivo = dt?.files?.[0]
      || [...(dt?.items || [])].find(i => i.kind === 'file')?.getAsFile();
    if (arquivo && onArquivo) { e.preventDefault(); onArquivo(arquivo); return; }

    const texto = dt?.getData('text/plain');
    if (texto === undefined || texto === null) return;
    e.preventDefault();
    // `insertText` preserva a pilha de desfazer do navegador — inserir nó na
    // mão faz o Ctrl+Z pular a colagem inteira.
    document.execCommand('insertText', false, texto);
    emitir(false);
  };

  const aoSoltar = (e) => {
    if (disabled) return;
    const arquivo = e.dataTransfer?.files?.[0];
    if (arquivo && onArquivo) { e.preventDefault(); onArquivo(arquivo); return; }
    // Texto arrastado de outro lugar entra como texto; o navegador cuida.
  };

  // Enter cria <br>, e não um <div> novo: mantém a estrutura previsível e a
  // serialização estável entre navegadores.
  const aoTeclar = (e) => {
    onKeyDown?.(e);
    if (e.defaultPrevented || disabled) return;
    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      document.execCommand('insertLineBreak');
      emitir(false);
    }
  };

  // O que a tela precisa poder fazer de fora: saber ONDE o cursor está (para
  // inserir a imagem ali) e devolver o foco depois.
  useImperativeHandle(refExterna, () => ({
    get elemento() { return interno.current; },
    // Cursor de agora; se o foco já saiu (a pessoa clicou no ícone), o último
    // que este campo viu. Sem isso a imagem cairia sempre no fim do texto.
    cursor() {
      const agora = offsetDoCursor(interno.current);
      return agora === null ? cursorGuardado.current : agora;
    },
    /** Seleção de agora; se o foco já saiu, a última que este campo viu. */
    selecao() {
      const agora = selecaoDoTexto(interno.current);
      return agora === null ? selecaoGuardada.current : agora;
    },
    /** Põe a seleção de volta num intervalo de caracteres. */
    selecionar(inicio, fim) {
      const alvo = interno.current;
      if (!alvo) return;
      alvo.focus();
      posicionarCursor(alvo, inicio);
      if (fim > inicio && window.getSelection) {
        const sel = window.getSelection();
        // Estende a seleção caractere a caractere: é a forma que funciona em
        // qualquer navegador sem remapear os nós do DOM na mão.
        for (let i = 0; i < fim - inicio; i += 1) sel.modify('extend', 'forward', 'character');
      }
    },
    focar(offset) {
      const alvo = interno.current;
      if (!alvo) return;
      alvo.focus();
      if (Number.isInteger(offset)) posicionarCursor(alvo, offset);
    },
    focus() { interno.current?.focus(); },
  }), []);

  return (
    <div
      {...resto}
      ref={interno}
      className={`ct-editor ${className}`}
      style={style}
      contentEditable={!disabled}
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      // Explícito: um campo de texto tem de ser alcançável pelo Tab como
      // qualquer outro. O navegador já faz isso com contenteditable, mas sem o
      // atributo o comportamento depende da implementação.
      tabIndex={disabled ? -1 : 0}
      data-placeholder={placeholder}
      data-vazio={value ? 'nao' : 'sim'}
      onInput={() => emitir(false)}
      onBlur={() => {
        cursorGuardado.current = offsetDoCursor(el());
        selecaoGuardada.current = selecaoDoTexto(el());

        // Só emite se o DOM já reflete o `value` de agora.
        //
        // Colar uma imagem muda o texto POR FORA (o marcador entra em `value`)
        // e abre o pedido de legenda, que leva o foco. O blur dispara nesse
        // instante — antes de o efeito remontar o DOM. Serializar aqui devolvia
        // ao pai a versão VELHA do texto, sem o marcador, e a imagem
        // desaparecia: era o Ctrl+V que "não anexava" enquanto o botão de
        // arquivo funcionava (ali o foco já tinha saído antes).
        if (emitido.current === value) emitir(true);
      }}
      onPaste={aoColar}
      onDrop={aoSoltar}
      onDragOver={(e) => { if (onArquivo && !disabled) e.preventDefault(); }}
      onKeyDown={aoTeclar}
    />
  );
});

export default EditorTexto;
